import type { ContentObjectInformation } from '@mbrtargeting/metatag-shared-types';
import { addCssToElement } from '@mbrtargeting/metatag-utils';
import { Registry } from '../../loader/di/registry.js';
import { MetaTag } from '../../loader/essentials/init-metatag.js';
import { logger } from '../../loader/essentials/logger.js';
import { LifecycleAction, LifecycleState } from '../modules/adslot-controller/adslot-lifecycle-state.js';
import { Adslot } from '../modules/adslot-controller/adslot.js';
import { CONTENT_OBJECT_WATCHER } from '../token.js';
import { getIdsForAdSlot } from '../utils/adslot-data-store.js';
import { EffectFunction } from './index.js';

const log = logger.logGroup({ prefix: 'PinToBottom Effect' });

declare const window: Window & {
    SDG?: MetaTag;
};

/**
 * Creates close button for stickyFooter
 * @param onclick
 */
const createCloseButton = (onclick: () => void): HTMLButtonElement => {
    const button = document.createElement('button');
    button.classList.add('close-sticky-ad');
    button.onclick = onclick;
    button.onkeydown = onclick; //necessary for screen readers to function properly
    return button;
};

//possible conflict with publisher elements, when other sticky elements are positioned at the bottom of the screen, like social media buttons. No solution for this now, besides overwriting sdgStickyFooter class in websiteConfig with important bottom value
const stickyFooterButtonCss = `
    .close-sticky-ad {
        --sdgCloseCross: clamp(8px, 1.25rem, 16px);
        --sdgCloseHeight: calc(var(--sdgCloseCross) * 2);
        cursor:pointer;
        position:absolute;
        top: calc( -1 * var(--sdgCloseHeight));
        width: calc(var(--sdgCloseCross) * 1.75);
        height: var(--sdgCloseHeight);
        right: 0;
        background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg viewBox="341 8 13 13" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath fill="%234F4F4F" d="M354 9.31L352.69 8l-5.19 5.19L342.31 8 341 9.31l5.19 5.19-5.19 5.19 1.31 1.31 5.19-5.19 5.19 5.19 1.31-1.31-5.19-5.19z" fill-rule="evenodd"/%3E%3C/svg%3E');
        background-size: var(--sdgCloseCross) var(--sdgCloseCross);
        background-position: 60% 50%;
        background-color: #fff;
        background-repeat: no-repeat;
        box-shadow: 0 -1px 1px 0 rgba(0,0,0,0.2);
        border: none;border-radius: 12px 0 0 0;
    }
    .sdgStickyFooter {
        position:fixed;
        z-index:5000;
        bottom:0px;
        left: var(--pintobottom-stabilized-left, 0px);
        background-color: white;
        box-shadow: 10px 10px 5px 10px rgba(0,0,0,0.2);
        padding-top:4px;
        width: var(--pintobottom-stabilized-width, 100%);
        min-height: 54px;
    }
    .sdgStickyFooter.sdgKeyboardActive {
        display: none;
    }
    @media (prefers-color-scheme: dark) {
        .close-sticky-ad {
            background-color: #111;
            box-shadow: 0 -1px 1px 0 rgba(255,255,255,0.2);
        }
        .sdgStickyFooter {
            background-color: #111;
            box-shadow: 10px 10px 5px 10px rgba(255,255,255,0.2);
        }
    }
`;

/**
 * when slot is filled, start stickyFooter by appending closeButton and applying style class "sdgStickyFooter".
 * Clicking the closeButton will unregister the adslot from the website (cleaning up this effect in same step)
 * @param adslot {Adslot}
 */
export const PIN_TO_BOTTOM: EffectFunction = (adslot: Adslot) => {
    const { containerNode, adserverAnchor, slotName } = adslot;
    log.debug('%s: add effect', [slotName.toUpperCase()]);
    const { stabilizeWidthToContent = false } = adslot.getPositionSettings();
    const contentObjectWatcher = Registry.lookup(CONTENT_OBJECT_WATCHER);

    const control = new AbortController();

    const fitToContentObject = ({ left, width }: ContentObjectInformation) => {
        if (width > 0) containerNode.style.setProperty('--pintobottom-stabilized-width', `${width}px`);
        else containerNode.style.removeProperty('--pintobottom-stabilized-width');
        
        if (left > 0) containerNode.style.setProperty('--pintobottom-stabilized-left', `${left}px`);
        else containerNode.style.removeProperty('--pintobottom-stabilized-left');
    }

    // setup viewport listeners for keyboard detection
    if (window.visualViewport) {
        const detectKeyboard = () => {
            const keyboardActive = (window.visualViewport?.offsetTop ?? 0) > 50;
            if (keyboardActive) containerNode.classList.add('sdgKeyboardActive');
            else containerNode.classList.remove('sdgKeyboardActive');
        }
        // initially detect keyboard state
        detectKeyboard();
        // add listeners to visualViewport to detect keyboard opening/closing
        window.visualViewport.addEventListener('resize', detectKeyboard, { signal: control.signal });
        window.visualViewport.addEventListener('scroll', detectKeyboard, { signal: control.signal });
    }

    // react to changes in contentObject (especially width and left)
    stabilizeWidthToContent && contentObjectWatcher.contentObject$
        .filter(({ element }) => !!element)
        .subscribe((contentObjectInfo) => {
            fitToContentObject(contentObjectInfo)
        }, { signal: control.signal });

    // close button should remain on adslot, for the whole duration of the effect and just be turned visible/invisible on demand
    const closeButton = adserverAnchor.appendChild(createCloseButton(() => {
        window.SDG!.Publisher.unregisterSlot(slotName);
    }));

    const show = (reason: string) => {
        log.debug('%s: show due to %s', [slotName.toUpperCase(), reason]);
        containerNode.classList.remove('sdgCloaked');
        closeButton.classList.remove('sdgCloaked');
    }

    const hide = (reason: string) => {
        log.debug('%s: hide due to %s', [slotName.toUpperCase(), reason]);
        containerNode.classList.add('sdgCloaked');
        closeButton.classList.add('sdgCloaked');
    }

    const styleNode = addCssToElement(stickyFooterButtonCss, containerNode);
    containerNode.classList.add('sdgStickyFooter');
    // hide until adslot is filled by an actual ad, otherwise we bother user with an empty container
    hide('waiting for creative');

    // when ad is starting to render, show adslot
    adslot.lifecycle.onAction(LifecycleAction.FILL, async () => {
        // if specific SSPs render their own close button and layer, suppress ours
        const { lastSsp } = getIdsForAdSlot(slotName); //potentially value is not set yet in current ad impression, and might use value from former imp
        if (lastSsp === 'ogury' || lastSsp === 'gumgum' || lastSsp === 'seedtag') {
            hide('provided by 3rd party');
        } else {
            containerNode.style.cssText = ""; //reverse inline styles set by other SSPs
            stabilizeWidthToContent && fitToContentObject(contentObjectWatcher.contentObject)
            show('creative rendered');
        }
    }, control);

    // when reload has no creative hide adslot
    adslot.lifecycle.onState(LifecycleState.EMPTY, async () => hide('empty adslot'), control);
    // when adslot is blocked, hide adslot
    adslot.lifecycle.onState(LifecycleState.BLOCKED, async () => hide('blocked adslot'), control);

    // clean up all possible css classes set by script. Possible collision with other effects using sdCloaked.
    return () => {
        log.debug('%s: remove effect', [slotName.toUpperCase()]);
        control.abort();
        closeButton.parentNode?.removeChild(closeButton);
        styleNode.parentNode?.removeChild(styleNode);
        containerNode.classList.remove('sdgStickyFooter');
        containerNode.classList.remove('sdgCloaked');
    };
};
